import React, { useState, useEffect, useMemo } from "react";
import { useLocalizedNavigate as useNavigate } from "@/hooks/useLocalizedNavigate";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight, ChevronDown, Mail, Target, Sparkles, Globe, Languages } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/providers/ThemeProvider";
import { tokens } from "@/lib/theme";
import { JobsContent, t } from "@/content/jobs";
import CityTicker from "@/components/careers/CityTicker";
import RoleCard from "@/components/careers/RoleCard";
import ProviderPitchCard from "@/components/careers/ProviderPitchCard";
import { Seo } from "@/components/seo/Seo";
import { type } from "@/lib/typography";
import { PublicShell } from "@/components/layouts/PublicShell";

const ICONS = { Target, Sparkles, Globe, Languages };
const CITIES = ["LAGOS", "DAKAR", "CASABLANCA", "NAIROBI", "SÃO PAULO", "REMOTE"];
const APPLY_URL = import.meta.env.VITE_CAREERS_APPLY_URL;


function adaptJob(row) {
  return {
    id: row.id,
    slug: row.slug,
    kind: row.kind,
    number: row.sort_order ? String(row.sort_order).padStart(2, "0") : "00",
    department: { en: row.department_label_en || row.department, fr: row.department_label_fr || row.department, es: "[TODO]", pt: "[TODO]" },
    title: { en: row.title_en, fr: row.title_fr || row.title_en, es: row.title_es || row.title_en, pt: row.title_pt || row.title_en },
    short_pitch: { en: row.short_pitch_en, fr: row.short_pitch_fr || row.short_pitch_en, es: row.short_pitch_es || row.short_pitch_en, pt: row.short_pitch_pt || row.short_pitch_en },
    location: { en: row.location_label_en, fr: row.location_label_fr || row.location_label_en, es: row.location_label_es || row.location_label_en, pt: row.location_label_pt || row.location_label_en },
    urgency: row.urgency,
    external_apply_url: row.external_apply_url
  };
}

export default function Careers() {
  const { i18n, t: tr } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);
  const themeCtx = useTheme?.();
  const resolvedTheme = (themeCtx?.theme === "dark" || (themeCtx?.theme === "system" && typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches)) ? "dark" : "light";
  const T = useMemo(() => tokens(resolvedTheme), [resolvedTheme]);
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [cityFilter, setCityFilter] = useState("ALL");

  useEffect(() => {
    supabase.from("job_postings")
      .select("*")
      .eq("active", true)
      .order("sort_order")
      .then(({ data, error }) => {
        if (error) { console.error(error); return; }
        setJobs((data || []).map(adaptJob));
      });
  }, []);

  const matchesCity = (j) => {
    if (cityFilter === "ALL") return true;
    const loc = (j.location?.en || "").toUpperCase();
    return loc.includes(cityFilter);
  };
  const fteJobs = jobs.filter((j) => j.kind === "fte" && matchesCity(j));
  const providerJobs = jobs.filter((j) => j.kind === "provider" && matchesCity(j));
  const scrollToRoles = () => document.getElementById("founding-roles")?.scrollIntoView({ behavior: "smooth" });

  const faqJsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (JobsContent.faq?.items || []).map((item) => ({
      "@type": "Question",
      name: t(item.q, lang),
      acceptedAnswer: { "@type": "Answer", text: t(item.a, lang) },
    })),
  }), [lang]);

  return (
    <PublicShell>
      <Seo title={tr("careers.seoTitle")} description={tr("careers.seoDescription")} jsonLd={faqJsonLd} />
      <div style={{
        color: T.text,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        transition: "background-color 300ms, color 300ms"
      }}>
        {/* CITY TICKER */}
        <section style={{ background: T.heroGradient, paddingTop: 24, paddingBottom: 24 }}>
          <CityTicker T={T} cities={JobsContent.cities} />
        </section>

        {/* FOUNDING FOUR */}
        <section id="founding-roles" style={{ padding: "clamp(80px, 12vh, 140px) clamp(20px, 4vw, 48px)", backgroundColor: T.bg }}>
          <div style={{ maxWidth: 1280, margin: "0 auto" }}>
            <div style={{ maxWidth: 720, marginBottom: 72 }}>
              <div style={{ ...type.eyebrow, color: T.primary, marginBottom: 20 }}>─── {t(JobsContent.fte_section.eyebrow, lang)}</div>
              <h2 style={{
                ...type.displayLg,
                color: T.text, marginBottom: 20, marginTop: 0
              }}>{t(JobsContent.fte_section.h2, lang)}</h2>
              <p style={{ ...type.bodyLg, color: T.textMute, margin: 0 }}>
                {t(JobsContent.fte_section.sub, lang)}
              </p>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 32 }}>
              {["ALL", ...CITIES].map((c) => {
                const active = cityFilter === c;
                const label = c === "ALL"
                  ? tr("careers.chip.all", "ALL")
                  : c === "REMOTE"
                    ? tr("careers.chip.remote", "REMOTE")
                    : c;
                return (
                  <button key={c} type="button" onClick={() => setCityFilter(c)} style={{
                    padding: "8px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, letterSpacing: "0.04em",
                    border: `1px solid ${active ? T.primary : T.border}`,
                    backgroundColor: active ? T.primary : "transparent",
                    color: active ? T.textInverse : T.text, cursor: "pointer", transition: "all 150ms"
                  }}>{label}</button>
                );
              })}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 24 }}>
              {fteJobs.length === 0 ? (
                <p style={{ color: T.textMute, fontSize: 14 }}>{tr("careers.emptyState")}</p>
              ) : fteJobs.map((job) => (
                <RoleCard key={job.id} job={job} lang={lang} T={T} onClick={(slug) => APPLY_URL ? window.open(APPLY_URL, "_blank") : navigate(`/careers/${slug}`)} />
              ))}
            </div>
          </div>
        </section>

        {/* MARKETPLACE */}
        <section style={{
          padding: "clamp(80px, 12vh, 140px) clamp(20px, 4vw, 48px)",
          backgroundColor: T.bgSubtle, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`
        }}>
          <div style={{ maxWidth: 1280, margin: "0 auto" }}>
            <div style={{
              display: "grid", gridTemplateColumns: "minmax(280px, 1fr) 1fr",
              gap: "clamp(40px, 6vw, 80px)", alignItems: "start"
            }} className="bp-provider-grid">
              <div>
                <div style={{ ...type.eyebrow, color: T.amber, marginBottom: 20 }}>─── {t(JobsContent.provider_section.eyebrow, lang)}</div>
                <h2 style={{
                  ...type.displayMd,
                  color: T.text, marginBottom: 24, marginTop: 0
                }}>{t(JobsContent.provider_section.h2, lang)}</h2>
                <p style={{ ...type.body, color: T.textMute, margin: 0 }}>
                  {t(JobsContent.provider_section.manifesto, lang)}
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {providerJobs.map((job) => (
                  <ProviderPitchCard key={job.id} job={job} lang={lang} T={T} onClick={(slug) => navigate(`/careers/${slug}`)} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* WHY JOIN */}
        <section style={{ padding: "clamp(80px, 10vh, 120px) clamp(20px, 4vw, 48px)", backgroundColor: T.bg }}>
          <div style={{ maxWidth: 1280, margin: "0 auto" }}>
            <div style={{ maxWidth: 640, marginBottom: 56 }}>
              <div style={{ ...type.eyebrow, color: T.textMute, marginBottom: 20 }}>─── {t(JobsContent.why_join.eyebrow, lang)}</div>
              <h2 style={{
                ...type.displaySm,
                color: T.text, margin: 0
              }}>{t(JobsContent.why_join.h2, lang)}</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 0 }}>
              {JobsContent.why_join.cards.map((c, i) => {
                const Icon = ICONS[c.icon] || Target;
                return (
                  <div key={i} style={{
                    padding: "32px 28px",
                    borderLeft: i > 0 ? `1px solid ${T.border}` : "none",
                    borderTop: `1px solid ${T.border}`
                  }}>
                    <Icon size={20} color={T.primary} strokeWidth={2} style={{ marginBottom: 16 }} />
                    <h3 style={{ ...type.panelTitle, color: T.text, marginBottom: 10, marginTop: 0 }}>
                      {t(c.title, lang)}
                    </h3>
                    <p style={{ ...type.bodySm, color: T.textMute, margin: 0 }}>
                      {t(c.body, lang)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ padding: "clamp(80px, 10vh, 120px) clamp(20px, 4vw, 48px)", backgroundColor: T.bgSubtle, borderTop: `1px solid ${T.border}` }}>
          <div style={{ maxWidth: 760, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div style={{ ...type.eyebrow, color: T.textMute, marginBottom: 16 }}>{t(JobsContent.faq.eyebrow, lang)}</div>
              <h2 style={{
                ...type.displaySm,
                color: T.text, margin: 0
              }}>{t(JobsContent.faq.h2, lang)}</h2>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {JobsContent.faq.items.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} style={{
                    borderRadius: 16, backgroundColor: T.bgElev, border: `1px solid ${isOpen ? T.primary : T.border}`,
                    overflow: "hidden", transition: "border-color 200ms"
                  }}>
                    <button onClick={() => setOpenFaq(isOpen ? null : i)}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "20px 24px", backgroundColor: "transparent", border: "none", cursor: "pointer",
                        textAlign: "left", color: T.text, fontWeight: 500, fontSize: 15
                      }}>
                      <span>{t(item.q, lang)}</span>
                      <ChevronDown size={18} style={{
                        color: T.textFaint, flexShrink: 0, marginLeft: 16,
                        transition: "transform 200ms", transform: isOpen ? "rotate(180deg)" : "rotate(0)"
                      }} />
                    </button>
                    {isOpen && (
                      <div style={{ padding: "0 24px 20px", color: T.textMute, fontSize: 14, lineHeight: 1.65 }}>
                        {t(item.a, lang)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FOOTER CTA */}
        <section style={{
          background: `linear-gradient(135deg, ${T.primary} 0%, #3B1A9F 100%)`,
          padding: "clamp(60px, 10vh, 100px) clamp(20px, 4vw, 48px)"
        }}>
          <div style={{ maxWidth: 880, margin: "0 auto", textAlign: "center" }}>
            <Mail size={32} color="#FFB547" strokeWidth={2} style={{ marginBottom: 20 }} />
            <h3 style={{
              ...type.displaySm,
              color: "#FFFFFF", marginTop: 0, marginBottom: 20
            }}>{t(JobsContent.footer_cta.intro, lang)}</h3>
            <a href="mailto:careers@bootpreneurs.com" style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              fontSize: "clamp(1.125rem, 2vw, 1.5rem)", fontWeight: 600, color: "#FFFFFF",
              textDecoration: "none", borderBottom: "2px solid #FFB547", paddingBottom: 4,
              fontFamily: "ui-monospace, Menlo, monospace"
            }}>careers@bootpreneurs.com<ArrowUpRight size={20} strokeWidth={2.5} /></a>
          </div>
        </section>
      </div>
    </PublicShell>
  );
}
